import { useState, useEffect, useCallback, useMemo } from "react";
import { IDataTableV2MasterColumn } from "../../interfaces/IDataTableV2Props";
import { getBackendTableColumnsAsync } from "../../../../services/general.services";
import { IDataTableV2DateSearchFilterConfig } from "./SearchFilter.types";

const formatLabel = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const DATE_KEYWORDS = ["_ts", "date"];

const inferColumnType = (key: string, type: any): string => {
  if (type) return type;

  const lower = key.toLowerCase();
  return DATE_KEYWORDS.some((keyword) => lower.includes(keyword))
    ? "date"
    : "text";
};

const buildColumns = (columns: { key: string; type: string | undefined }[]) =>
  columns
    .filter((key) => !key.key.toLowerCase().includes("_uuid"))
    .map((key) => ({
      value: key.key,
      label: formatLabel(key.key),
      type: inferColumnType(key.key, key.type),
    }))
    .sort((a, b) => a.value.localeCompare(b.value));

const EMPTY_FILTER_CONFIG: IDataTableV2DateSearchFilterConfig = {
  columns: [],
  columnOptions: {},
};

export const useFilterOperation = (
  masterColumns: IDataTableV2MasterColumn[],
  tableName?: string,
) => {
  const [filterState, setFilterState] =
    useState<IDataTableV2DateSearchFilterConfig>(EMPTY_FILTER_CONFIG);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const masterColumnKeys = useMemo(
    () => masterColumns.map((col) => ({ key: col.key, type: col.type })),
    [masterColumns],
  );

  const fetchFilterColumns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // const backendColumns = tableName ? await getBackendTableColumnsAsync(tableName) : []

      // const finalColumns = backendColumns.length > 0 ? backendColumns : masterColumnKeys
      const finalColumns = masterColumnKeys;

      setFilterState({
        columns: buildColumns(finalColumns),
        columnOptions: {},
      });
    } catch (err) {
      console.error("Error fetching filter columns:", err);
      setError("Failed to load filter columns");
      setFilterState({
        columns: buildColumns(masterColumnKeys),
        columnOptions: {},
      });
    } finally {
      setLoading(false);
    }
  }, [tableName, masterColumnKeys]);

  useEffect(() => {
    fetchFilterColumns();
  }, [fetchFilterColumns]);

  return {
    advanceConfiguration: filterState,
    loading,
    error,
    refetch: fetchFilterColumns,
  };
};
